import { useEffect, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { ErrorBox } from "../support/ErrorBox";
import FormControl from "@mui/material/FormControl";
import { FormikProps } from "formik";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { CustomerBusinessData as Type } from "../customer-model";
import Typography from "@mui/material/Typography";
import countriesJSON from "../../../lib/language/countries.json";
import { getSmartyAutocompleteEndpoint } from "@components/request-access/address-smarty-api";
import { useTranslation } from "next-i18next";

interface Props {
  formik: FormikProps<Type>;
}

interface RegionType {
  countrySubdivision: string;
  postalCode: string;
}

interface CountryType {
  id: number;
  name: string;
  iso3: string;
  iso2: string;
  phone_code: string;
  capital: string;
  currency: string;
  regionType: RegionType;
}

interface AddressType {
  street_line: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
}

interface SmartyResponse {
  suggestions: AddressType[];
}

const countries = countriesJSON as CountryType[];

function getStateLabel(e: any) {
  // Set default
  let stateLabel = "request_access:customer_access.state";

  // Get the selected Country of Business
  const selectedCountryOfBusiness = e?.target?.value;

  if (selectedCountryOfBusiness) {
    // Get the corresponding item from countries
    const countryItem = countries.find((item) => {
      return item.name === selectedCountryOfBusiness;
    });
    const countrySubdivision = countryItem?.regionType?.countrySubdivision;

    if (countryItem && countrySubdivision && countrySubdivision.length > 0) {
      // Use the appropriate State label for the selected Country
      stateLabel = "request_access:customer_access." + countrySubdivision;
    }
  }

  return stateLabel;
}

function getZipCodeLabel(e: any) {
  // Set default
  let zipCodeLabel = "common:common.zip_code";

  // Get the selected Country of Business
  const selectedCountryOfBusiness = e?.target?.value;

  if (selectedCountryOfBusiness) {
    // Get the corresponding item from countries
    const countryItem = countries.find((item) => {
      return item.name === selectedCountryOfBusiness;
    });
    const postalCode = countryItem?.regionType?.postalCode;

    // If this country has "zip_code", then stay with the default we already set.
    // Otherwise, use "postal_code".
    if (countryItem && postalCode && postalCode !== "zip_code") {
      zipCodeLabel = "request_access:customer_access.postal_code";
    }
  }

  return zipCodeLabel;
}

/**
 * Customer Access step / screen.
 */
export function BusinessInformationStep({ formik }: Props) {
  const { t } = useTranslation();
  const [stateLabel, setStateLabel] = useState("");
  const [zipCodeLabel, setZipCodeLabel] = useState("");
  const [options, setOptions] = useState<readonly AddressType[]>([]);
  const [suggestions, setSuggestions] = useState<readonly string[]>([]);

  function populateLocationFields(address: string | null) {
    if (address && options) {
      const location: AddressType = options.filter((x) => {
        return x.street_line === address;
      })[0];
      if (location) {
        formik.setFieldValue("city", location.city);
        formik.setFieldValue("state", location.state);
        formik.setFieldValue("zipCode", Number(location.zipcode));
      }
    } else {
      cleanupFields();
    }
  }

  function cleanupFields() {
    formik.setFieldValue("city", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("zipCode", "");
  }

  function getAutoCompleteAddress(address: string) {
    fetchAutoCompleteOptions(address);
  }

  async function fetchAutoCompleteOptions(address: string) {
    const smartybaseURL = getSmartyAutocompleteEndpoint();
    const searchCriteria = address
      ? "&search=" + address.split(" ").join("+")
      : "1";
    const prefer_geolocation = "&prefer_geolocation=city";
    const url = `${smartybaseURL}${prefer_geolocation}${searchCriteria}`;
    let jsonResponse = "";

    await fetch(`${url}`, {
      cache: "no-cache",
    })
      .then((response) => response.text())
      .then((result) => (jsonResponse = result.toString()))
      .catch((error) => console.log("error", error));

    const parsedData = JSON.parse(jsonResponse) as SmartyResponse;
    const suggestionsResults = parsedData.suggestions;
    setOptions(suggestionsResults);
    const list: string[] = [];
    if (suggestionsResults) {
      suggestionsResults.forEach((x) => {
        list.push(x.street_line + ", " + x.state + " " + x.zipcode);
      });
    }
    setSuggestions(list);
  }

  useEffect(() => {
    setStateLabel("request_access:customer_access.state");
    setZipCodeLabel("common:common.zip_code");
  }, []);

  useEffect(() => {
    if (formik.values.countryOfBusiness.toLowerCase() === "united states") {
      getAutoCompleteAddress(formik.values.address.split(",")[0]);
    } else {
      setSuggestions([]);
      cleanupFields();
    }
    // TODO: fix this hook dependency inconsistency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.address, formik.values.countryOfBusiness]);

  return (
    <Box>
      <Box
        sx={{
          marginBottom: "20px",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: "1.75em",
            fontWeight: "bold",
          }}
        >
          {t("common:common.request_access")}
        </Typography>
      </Box>
      <Box sx={{ fontSize: "14px", mb: "12px", textAlign: "left" }}>
        {t("request_access:customer_access.business_information")}
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            marginBottom: "20px",
          }}
        >
          {/* Company Name */}
          <InputLabel
            sx={{ textAlign: "left", mt: "1rem" }}
            htmlFor="companyName"
          >
            {t("request_access:customer_access.company_name")}
          </InputLabel>
          <TextField
            fullWidth
            id="companyName"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.companyName && formik.touched.companyName && (
            <ErrorBox>{formik.errors.companyName}</ErrorBox>
          )}

          {/* Country of Business */}
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              htmlFor="countryOfBusiness"
              sx={{ textAlign: "left", mt: "1rem" }}
            >
              {t("request_access:customer_access.business_country")}
            </InputLabel>
            <FormControl fullWidth>
              <Select
                sx={{
                  paddingLeft: "0",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
                id="countryOfBusiness"
                name="countryOfBusiness"
                value={formik.values.countryOfBusiness}
                onChange={async (e) => {
                  setStateLabel(getStateLabel(e));
                  setZipCodeLabel(getZipCodeLabel(e));
                  formik.handleChange(e);
                  formik.setFieldValue("address", "");
                }}
              >
                {countries.map((item: { name: string }, index: number) => (
                  <MenuItem key={index} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formik.errors.countryOfBusiness &&
              formik.touched.countryOfBusiness && (
                <ErrorBox>{formik.errors.countryOfBusiness}</ErrorBox>
              )}
          </Box>

          {/* Address */}
          <Box>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="address"
            >
              {t("common:common.address")}
            </InputLabel>
            <Autocomplete
              sx={{
                width: "100%",
                "& .MuiAutocomplete-clearIndicator": {
                  right: "10px", // Adjust position for clear icon
                  top: "50%", // Vertically center the icon
                  transform: "translateY(50%)", // Center alignment
                },
              }}
              autoComplete
              freeSolo
              options={suggestions}
              value={formik.values.address}
              id="address"
              noOptionsText="No locations"
              onInputChange={(event, newInputValue) => {
                formik.setFieldValue("address", newInputValue.split(",")[0]);
                populateLocationFields(newInputValue.split(",")[0]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.countryOfBusiness}
                />
              )}
            />
            {formik.errors.address && formik.touched.address && (
              <ErrorBox>{formik.errors.address}</ErrorBox>
            )}
          </Box>

          {/* City */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="city">
            {t("common:common.city")}
          </InputLabel>
          <TextField
            fullWidth
            id="city"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!formik.values.countryOfBusiness}
          />
          {formik.errors.city && formik.touched.city && (
            <ErrorBox>{formik.errors.city}</ErrorBox>
          )}

          {/* State */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="state">
            {t(stateLabel)}
          </InputLabel>
          <TextField
            fullWidth
            id="state"
            value={formik.values.state}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!formik.values.countryOfBusiness}
          />
          {formik.errors.state && formik.touched.state && (
            <ErrorBox>{formik.errors.state}</ErrorBox>
          )}

          {/* Zip Code */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="zipCode">
            {t(zipCodeLabel)}
          </InputLabel>
          <TextField
            fullWidth
            id="zipCode"
            value={formik.values.zipCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!formik.values.countryOfBusiness}
          />
          {formik.errors.zipCode && formik.touched.zipCode && (
            <ErrorBox>{formik.errors.zipCode}</ErrorBox>
          )}
        </Box>

        <Box mt={12}>
          <Button
            sx={{ width: "100%", mb: 4 }}
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
          >
            {t("common:common.continue")}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
